import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Building2, ArrowLeft, Calculator, Home, DollarSign,
  Calendar, Sun, Snowflake, FileText
} from "lucide-react";
import { ChatToggleButton } from "@/components/AIChatWidget";
import axios from "axios";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from "recharts";
import { FREE_REPORT_GATE_MESSAGE, isFreeTier, normalizeTier } from "@/utils/planMessaging";
import { useReportQuota } from "@/hooks/useReportQuota";

const AirbnbCalculator = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const userTier = normalizeTier(user?.tier);
  const isFreeInvestor = isFreeTier(userTier);
  const {
    reportsUsedThisMonth,
    isStarter,
    isProLike,
    starterLimitReached,
    reportPlanText,
  } = useReportQuota(Boolean(user && token));
  
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  
  const [formData, setFormData] = useState({
    property_name: "",
    address: "",
    purchase_price: 350000,
    down_payment_percent: 20,
    loan_term_years: 30,
    interest_rate: 7,
    occupancy_rates: [50, 50, 60, 70, 80, 85, 90, 90, 80, 70, 60, 50],
    nightly_rates: [150, 150, 175, 200, 225, 250, 275, 275, 225, 200, 175, 150],
    cleaning_fee_per_stay: 100,
    avg_stay_nights: 3,
    property_taxes_annual: 4200,
    insurance_annual: 2400,
    hoa_monthly: 0,
    utilities_monthly: 250,
    supplies_monthly: 100,
    management_percent: 20,
    maintenance_percent: 5,
    platform_fee_percent: 3,
    // NEW: STR-specific expenses (addresses pain point about missing expenses)
    cleaning_cost_per_turn: 75,        // Host-paid cleaning cost
    linens_replacement_annual: 500,    // Annual linen/towel replacement
    restocking_per_turn: 15,           // Toiletries, coffee, etc.
    guest_damage_reserve_percent: 2,   // Reserve for unreported damage
    photography_annual: 200,           // Professional photos
    dynamic_pricing_tools: 40,         // PriceLabs, Wheelhouse, etc.
    channel_manager_monthly: 0,        // If using multiple platforms
    permit_license_annual: 0,          // STR permits/licenses
    extra_insurance_annual: 0,         // STR-specific liability coverage
    host_labor_hours_weekly: 5,        // Messaging, check-ins, issues
    host_hourly_rate: 25               // Value of your time
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setAnalysis(null);
  };

  const handleMonthlyChange = (field, month, value) => {
    const newArray = [...formData[field]];
    newArray[month] = parseFloat(value) || 0;
    setFormData(prev => ({ ...prev, [field]: newArray }));
    setAnalysis(null);
  };

  const calculateAnalysis = async () => {
    if (!formData.property_name) {
      toast.error("Please enter a property name");
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(`${API}/analysis/airbnb`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalysis(response.data);
      toast.success("Analysis calculated and saved!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to calculate analysis");
    } finally {
      setSaving(false);
    }
  };

  const handleReportClick = () => {
    if (isFreeInvestor) {
      toast.error(FREE_REPORT_GATE_MESSAGE);
      return;
    }
    if (starterLimitReached) {
      toast.error("Monthly report limit reached. Upgrade to Pro for unlimited reports.");
      return;
    }
    navigate(`/report/airbnb/${analysis.id}`);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  const formatPercent = (value) => {
    return `${(value || 0).toFixed(1)}%`;
  };

  return (
    <div className="min-h-screen bg-slate-900" data-testid="airbnb-calculator-page">
      {/* Header */}
      <header className="glass border-b border-slate-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate("/dashboard")}
                className="text-slate-400 hover:text-white"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Dashboard
              </Button>
              <div className="h-6 w-px bg-slate-700"></div>
              <h1 className="text-lg font-semibold text-white font-['Manrope']">Airbnb Calculator</h1>
            </div>
            <div className="flex items-center gap-3">
              <ChatToggleButton />
              {analysis && (
                <Button
                  variant="outline"
                  onClick={handleReportClick}
                  disabled={starterLimitReached}
                  className="border-slate-700 text-slate-300 hover:text-white"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Report
                </Button>
              )}
              {analysis && (
                <div className="hidden md:flex flex-col leading-tight">
                  <span className="text-xs text-slate-300">{reportPlanText}</span>
                  {isStarter && (
                    <span className="text-xs text-slate-500">{reportsUsedThisMonth} of 5 reports used this month</span>
                  )}
                  {isProLike && (
                    <span className="text-xs text-slate-500">Unlimited report generation</span>
                  )}
                </div>
              )}
              {analysis && starterLimitReached && (
                <Button
                  size="sm"
                  onClick={() => navigate('/upgrade')}
                  className="bg-amber-500 hover:bg-amber-600 text-slate-900"
                >
                  Upgrade to Pro
                </Button>
              )}
              <Button 
                onClick={calculateAnalysis}
                disabled={saving}
                className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
                data-testid="calculate-airbnb-btn"
              >
                {saving ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-slate-900 mr-2"></div>
                ) : (
                  <Calculator className="h-4 w-4 mr-2" />
                )}
                Calculate
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Input Form */}
          <div className="space-y-6">
            {/* Property Details */}
            <div className="glass-card rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Home className="h-5 w-5 text-amber-500" />
                Property Details
              </h3>
              <div className="space-y-4">
                <div>
                  <Label className="text-slate-300">Property Name *</Label>
                  <Input
                    value={formData.property_name}
                    onChange={(e) => handleInputChange('property_name', e.target.value)}
                    placeholder="Beach House Retreat"
                    className="bg-slate-950 border-slate-800 text-white"
                    data-testid="airbnb-property-name-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Purchase Price</Label>
                    <Input
                      type="number"
                      value={formData.purchase_price}
                      onChange={(e) => handleInputChange('purchase_price', parseFloat(e.target.value) || 0)}
                      className="bg-slate-950 border-slate-800 text-white font-mono"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Down Payment (%)</Label>
                    <Input
                      type="number"
                      value={formData.down_payment_percent}
                      onChange={(e) => handleInputChange('down_payment_percent', parseFloat(e.target.value) || 0)}
                      className="bg-slate-950 border-slate-800 text-white font-mono"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Interest Rate (%)</Label>
                    <Input
                      type="number"
                      step="0.125"
                      value={formData.interest_rate}
                      onChange={(e) => handleInputChange('interest_rate', parseFloat(e.target.value) || 0)}
                      className="bg-slate-950 border-slate-800 text-white font-mono"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Loan Term (Years)</Label>
                    <Select 
                      value={formData.loan_term_years.toString()} 
                      onValueChange={(v) => handleInputChange('loan_term_years', parseInt(v))}
                    >
                      <SelectTrigger className="bg-slate-950 border-slate-800 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-800 max-h-60">
                        <SelectItem value="10">10 Years</SelectItem>
                        <SelectItem value="15">15 Years</SelectItem>
                        <SelectItem value="20">20 Years</SelectItem>
                        <SelectItem value="25">25 Years</SelectItem>
                        <SelectItem value="30">30 Years</SelectItem>
                        <SelectItem value="35">35 Years</SelectItem>
                        <SelectItem value="40">40 Years</SelectItem>
                        <SelectItem value="50">50 Years</SelectItem>
                        <SelectItem value="75">75 Years</SelectItem>
                        <SelectItem value="100">100 Years</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>

            {/* Seasonal Rates */}
            <div className="glass-card rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Calendar className="h-5 w-5 text-cyan-500" />
                Monthly Occupancy & Rates
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr>
                      <th className="text-left py-2 px-2 text-xs text-slate-400 uppercase">Month</th>
                      <th className="text-left py-2 px-2 text-xs text-slate-400 uppercase">Occupancy %</th>
                      <th className="text-left py-2 px-2 text-xs text-slate-400 uppercase">Nightly Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {months.map((month, i) => (
                      <tr key={month} className="border-t border-slate-800">
                        <td className="py-2 px-2 text-slate-300 flex items-center gap-2">
                          {i >= 5 && i <= 8 ? <Sun className="h-3 w-3 text-amber-500" /> : <Snowflake className="h-3 w-3 text-cyan-500" />}
                          {month}
                        </td>
                        <td className="py-2 px-2">
                          <Input
                            type="number"
                            value={formData.occupancy_rates[i]}
                            onChange={(e) => handleMonthlyChange('occupancy_rates', i, e.target.value)}
                            className="bg-slate-950 border-slate-800 text-white font-mono h-8 w-20"
                          />
                        </td>
                        <td className="py-2 px-2">
                          <Input
                            type="number"
                            value={formData.nightly_rates[i]}
                            onChange={(e) => handleMonthlyChange('nightly_rates', i, e.target.value)}
                            className="bg-slate-950 border-slate-800 text-white font-mono h-8 w-24"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Expenses */}
            <div className="glass-card rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-emerald-500" />
                Operating Expenses
              </h3>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Cleaning Fee/Stay (Guest pays)</Label>
                    <Input
                      type="number"
                      value={formData.cleaning_fee_per_stay}
                      onChange={(e) => handleInputChange('cleaning_fee_per_stay', parseFloat(e.target.value) || 0)}
                      className="bg-slate-950 border-slate-800 text-white font-mono"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Avg Stay (Nights)</Label>
                    <Input
                      type="number"
                      value={formData.avg_stay_nights}
                      onChange={(e) => handleInputChange('avg_stay_nights', parseFloat(e.target.value) || 1)}
                      className="bg-slate-950 border-slate-800 text-white font-mono"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Property Taxes/Year</Label>
                    <Input
                      type="number"
                      value={formData.property_taxes_annual}
                      onChange={(e) => handleInputChange('property_taxes_annual', parseFloat(e.target.value) || 0)}
                      className="bg-slate-950 border-slate-800 text-white font-mono"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Insurance/Year</Label>
                    <Input
                      type="number"
                      value={formData.insurance_annual}
                      onChange={(e) => handleInputChange('insurance_annual', parseFloat(e.target.value) || 0)}
                      className="bg-slate-950 border-slate-800 text-white font-mono"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-slate-300">Management %</Label>
                    <Input
                      type="number"
                      value={formData.management_percent}
                      onChange={(e) => handleInputChange('management_percent', parseFloat(e.target.value) || 0)}
                      className="bg-slate-950 border-slate-800 text-white font-mono"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Platform Fee %</Label>
                    <Input
                      type="number"
                      value={formData.platform_fee_percent}
                      onChange={(e) => handleInputChange('platform_fee_percent', parseFloat(e.target.value) || 0)}
                      className="bg-slate-950 border-slate-800 text-white font-mono"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Utilities/Mo</Label>
                    <Input
                      type="number"
                      value={formData.utilities_monthly}
                      onChange={(e) => handleInputChange('utilities_monthly', parseFloat(e.target.value) || 0)}
                      className="bg-slate-950 border-slate-800 text-white font-mono"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* NEW: STR-Specific Expenses Section */}
            <div className="glass-card rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
                <Home className="h-5 w-5 text-pink-500" />
                STR-Specific Costs
              </h3>
              <p className="text-xs text-slate-500 mb-4">Often missed expenses that impact real profitability</p>
              
              <div className="space-y-4">
                {/* Turnover Costs */}
                <div className="p-3 bg-slate-800/50 rounded-lg">
                  <div className="text-xs text-pink-500 font-semibold mb-2">Per-Turnover Costs</div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300 text-sm">Cleaning Cost/Turn (Host pays)</Label>
                      <Input
                        type="number"
                        value={formData.cleaning_cost_per_turn}
                        onChange={(e) => handleInputChange('cleaning_cost_per_turn', parseFloat(e.target.value) || 0)}
                        className="bg-slate-950 border-slate-800 text-white font-mono"
                        placeholder="75"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300 text-sm">Restocking/Turn</Label>
                      <Input
                        type="number"
                        value={formData.restocking_per_turn}
                        onChange={(e) => handleInputChange('restocking_per_turn', parseFloat(e.target.value) || 0)}
                        className="bg-slate-950 border-slate-800 text-white font-mono"
                        placeholder="15"
                      />
                    </div>
                  </div>
                </div>

                {/* Annual Costs */}
                <div className="p-3 bg-slate-800/50 rounded-lg">
                  <div className="text-xs text-cyan-500 font-semibold mb-2">Annual Costs</div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300 text-sm">Linens Replacement/Year</Label>
                      <Input
                        type="number"
                        value={formData.linens_replacement_annual}
                        onChange={(e) => handleInputChange('linens_replacement_annual', parseFloat(e.target.value) || 0)}
                        className="bg-slate-950 border-slate-800 text-white font-mono"
                        placeholder="500"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300 text-sm">Photography/Year</Label>
                      <Input
                        type="number"
                        value={formData.photography_annual}
                        onChange={(e) => handleInputChange('photography_annual', parseFloat(e.target.value) || 0)}
                        className="bg-slate-950 border-slate-800 text-white font-mono"
                        placeholder="200"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300 text-sm">Permit/License/Year</Label>
                      <Input
                        type="number"
                        value={formData.permit_license_annual}
                        onChange={(e) => handleInputChange('permit_license_annual', parseFloat(e.target.value) || 0)}
                        className="bg-slate-950 border-slate-800 text-white font-mono"
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300 text-sm">Extra STR Insurance/Year</Label>
                      <Input
                        type="number"
                        value={formData.extra_insurance_annual}
                        onChange={(e) => handleInputChange('extra_insurance_annual', parseFloat(e.target.value) || 0)}
                        className="bg-slate-950 border-slate-800 text-white font-mono"
                        placeholder="0"
                      />
                    </div>
                  </div>
                </div>

                {/* Software & Tools */}
                <div className="p-3 bg-slate-800/50 rounded-lg">
                  <div className="text-xs text-amber-500 font-semibold mb-2">Software & Tools</div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300 text-sm">Dynamic Pricing Tools/Mo</Label>
                      <Input
                        type="number"
                        value={formData.dynamic_pricing_tools}
                        onChange={(e) => handleInputChange('dynamic_pricing_tools', parseFloat(e.target.value) || 0)}
                        className="bg-slate-950 border-slate-800 text-white font-mono"
                        placeholder="40"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300 text-sm">Channel Manager/Mo</Label>
                      <Input
                        type="number"
                        value={formData.channel_manager_monthly}
                        onChange={(e) => handleInputChange('channel_manager_monthly', parseFloat(e.target.value) || 0)}
                        className="bg-slate-950 border-slate-800 text-white font-mono"
                        placeholder="0"
                      />
                    </div>
                  </div>
                </div>

                {/* Host Labor */}
                <div className="p-3 bg-slate-800/50 rounded-lg">
                  <div className="text-xs text-violet-500 font-semibold mb-2">Host Labor (Your Time)</div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300 text-sm">Hours/Week</Label>
                      <Input
                        type="number"
                        value={formData.host_labor_hours_weekly}
                        onChange={(e) => handleInputChange('host_labor_hours_weekly', parseFloat(e.target.value) || 0)}
                        className="bg-slate-950 border-slate-800 text-white font-mono"
                        placeholder="5"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300 text-sm">Your Hourly Rate</Label>
                      <Input
                        type="number"
                        value={formData.host_hourly_rate}
                        onChange={(e) => handleInputChange('host_hourly_rate', parseFloat(e.target.value) || 0)}
                        className="bg-slate-950 border-slate-800 text-white font-mono"
                        placeholder="25"
                      />
                    </div>
                  </div>
                  <div className="mt-2 text-xs text-slate-500">
                    Annual labor value: ${((formData.host_labor_hours_weekly || 0) * (formData.host_hourly_rate || 0) * 52).toLocaleString()}
                  </div>
                </div>

                {/* Damage Reserve */}
                <div>
                  <Label className="text-slate-300 text-sm">Guest Damage Reserve %</Label>
                  <Input
                    type="number"
                    value={formData.guest_damage_reserve_percent}
                    onChange={(e) => handleInputChange('guest_damage_reserve_percent', parseFloat(e.target.value) || 0)}
                    className="bg-slate-950 border-slate-800 text-white font-mono"
                    placeholder="2"
                  />
                  <p className="text-xs text-slate-500 mt-1">% of revenue for unreported damage/chargebacks</p>
                </div>
              </div>
            </div>
          </div>

          {/* Results */}
          <div className="space-y-6">
            {!analysis ? (
              <div className="glass-card rounded-xl p-12 text-center h-full flex flex-col items-center justify-center">
                <Calendar className="h-12 w-12 text-slate-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">Enter STR Details</h3>
                <p className="text-slate-400 mb-6">Fill in the form and click Calculate to see your Airbnb analysis</p>
                <Button 
                  onClick={calculateAnalysis}
                  className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
                >
                  <Calculator className="h-4 w-4 mr-2" />
                  Calculate Now
                </Button>
              </div>
            ) : (
              <>
                {/* Key Metrics */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="metric-card" data-testid="airbnb-monthly-cashflow">
                    <div className="text-sm text-slate-400 mb-1">Monthly Cash Flow</div>
                    <div className={`text-2xl font-bold font-mono ${analysis.results.monthly_cash_flow >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                      {formatCurrency(analysis.results.monthly_cash_flow)}
                    </div>
                  </div>
                  <div className="metric-card">
                    <div className="text-sm text-slate-400 mb-1">Annual Revenue</div>
                    <div className="text-2xl font-bold font-mono text-cyan-500">
                      {formatCurrency(analysis.results.annual_revenue)}
                    </div>
                  </div>
                  <div className="metric-card">
                    <div className="text-sm text-slate-400 mb-1">Cash-on-Cash</div>
                    <div className={`text-2xl font-bold font-mono ${analysis.results.cash_on_cash >= 0 ? 'text-amber-500' : 'text-red-500'}`}>
                      {formatPercent(analysis.results.cash_on_cash)}
                    </div>
                  </div>
                  <div className="metric-card">
                    <div className="text-sm text-slate-400 mb-1">Cap Rate</div>
                    <div className="text-2xl font-bold font-mono text-purple-500">
                      {formatPercent(analysis.results.cap_rate)}
                    </div>
                  </div>
                </div>

                {/* Additional Metrics */}
                <div className="glass-card rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-4">Performance Metrics</h3>
                  <div className="grid grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <div className="flex justify-between py-2 border-b border-slate-800">
                        <span className="text-slate-400">Avg Occupancy</span>
                        <span className="font-mono text-white">{formatPercent(analysis.results.avg_occupancy)}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-slate-800">
                        <span className="text-slate-400">Avg Nightly Rate</span>
                        <span className="font-mono text-white">{formatCurrency(analysis.results.avg_nightly_rate)}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-slate-800">
                        <span className="text-slate-400">Break-even Occupancy</span>
                        <span className="font-mono text-amber-500">{formatPercent(analysis.results.break_even_occupancy)}</span>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="flex justify-between py-2 border-b border-slate-800">
                        <span className="text-slate-400">Down Payment</span>
                        <span className="font-mono text-white">{formatCurrency(analysis.results.down_payment)}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-slate-800">
                        <span className="text-slate-400">Monthly Mortgage</span>
                        <span className="font-mono text-white">{formatCurrency(analysis.results.monthly_mortgage)}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-slate-800">
                        <span className="text-slate-400">Annual NOI</span>
                        <span className="font-mono text-emerald-500">{formatCurrency(analysis.results.noi)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Monthly Revenue Chart */}
                <div className="glass-card rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-4">Monthly Revenue</h3>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={analysis.monthly_breakdown}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis dataKey="month" stroke="#64748b" />
                        <YAxis stroke="#64748b" tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                          labelStyle={{ color: '#f1f5f9' }}
                          formatter={(value) => formatCurrency(value)}
                        />
                        <Bar dataKey="gross_income" name="Gross Income" fill="#06b6d4" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="net_income" name="Net Income" fill="#10b981" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Monthly Breakdown Table */}
                <div className="glass-card rounded-xl overflow-hidden">
                  <div className="p-4 border-b border-slate-800">
                    <h3 className="text-lg font-semibold text-white">Monthly Breakdown</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full data-table">
                      <thead>
                        <tr>
                          <th>Month</th>
                          <th>Occupancy</th>
                          <th>Nights</th>
                          <th>Gross</th>
                          <th>Net</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analysis.monthly_breakdown.map((month, index) => (
                          <tr key={index}>
                            <td>{month.month}</td>
                            <td className="font-mono">{month.occupancy_rate}%</td>
                            <td className="font-mono">{month.occupied_nights}</td>
                            <td className="font-mono">{formatCurrency(month.gross_income)}</td>
                            <td className={`font-mono ${month.net_income >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                              {formatCurrency(month.net_income)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AirbnbCalculator;
